import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  LayoutDashboard,
  Users as UsersIcon,
  ArrowDownToLine,
  ArrowUpFromLine,
  Award,
  LifeBuoy,
  Search,
  RefreshCcw,
  Check,
  X,
  ExternalLink,
  ShieldAlert,
  Crown,
  ChevronRight,
  LogOut,
  Copy,
  Wallet as WalletIcon,
  Plus,
  Minus,
  History,
  Loader2,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import adminApi, { ADMIN_TOKEN_KEY } from "@/lib/adminApi";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
const fmtUSD = (n) =>
  "$" +
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const shortAddr = (a) => {
  if (!a) return "—";
  const s = String(a);
  return s.length > 14 ? `${s.slice(0, 6)}…${s.slice(-4)}` : s;
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(d);
  }
};

const statusTone = (status) => {
  switch ((status || "").toLowerCase()) {
    case "confirmed":
    case "paid":
    case "completed":
    case "approved":
      return { bg: "rgba(34,197,94,0.12)", border: "rgba(34,197,94,0.40)", color: "#22C55E" };
    case "pending":
      return { bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.40)", color: "#F59E0B" };
    case "rejected":
    case "failed":
      return { bg: "rgba(248,113,113,0.12)", border: "rgba(248,113,113,0.40)", color: "#F87171" };
    default:
      return { bg: "rgba(124,58,237,0.10)", border: "rgba(124,58,237,0.30)", color: "#A78BFA" };
  }
};

const NAV_ITEMS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, idx: "01" },
  { id: "users", label: "Users", icon: UsersIcon, idx: "02" },
  { id: "deposits", label: "Deposits", icon: ArrowDownToLine, idx: "03" },
  { id: "withdrawals", label: "Withdrawals", icon: ArrowUpFromLine, idx: "04" },
  { id: "wallet-adjust", label: "Wallet Adjust", icon: WalletIcon, idx: "05" },
  { id: "ranks", label: "Rank Members", icon: Award, idx: "06" },
  { id: "support", label: "Support", icon: LifeBuoy, idx: "07" },
];

// ────────────────────────────────────────────────────────────────────────────
// 3D Stat Card
// ────────────────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, tone = "#A78BFA", icon: Icon }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="relative rounded-2xl p-4 overflow-hidden"
      style={{
        background:
          "linear-gradient(160deg, var(--surface-1) 0%, var(--surface-2) 100%)",
        border: "1px solid var(--card3d-border)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.10), 0 6px 16px -6px rgba(0,0,0,0.18)",
      }}
    >
      <div
        className="absolute inset-0 rounded-2xl pointer-events-none opacity-60"
        style={{
          background: `linear-gradient(135deg, ${tone}18 0%, transparent 55%)`,
        }}
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--text-3)]">
            {label}
          </div>
          <div
            className="mono text-2xl font-black mt-1 leading-none"
            style={{ color: "var(--text-1)" }}
          >
            {value}
          </div>
          {sub && (
            <div className="text-[11px] mt-1.5" style={{ color: tone }}>
              {sub}
            </div>
          )}
        </div>
        {Icon && (
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: `linear-gradient(180deg, ${tone}30 0%, ${tone}10 100%)`,
              border: `1px solid ${tone}55`,
              boxShadow: `inset 0 1px 0 rgba(255,255,255,0.20), 0 0 12px ${tone}33`,
            }}
          >
            <Icon size={18} style={{ color: tone }} />
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Row Card (used across listings)
// ────────────────────────────────────────────────────────────────────────────
function RowCard({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="relative rounded-xl p-3 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, var(--surface-1) 0%, var(--surface-2) 100%)",
        border: "1px solid var(--card3d-border)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 10px -4px rgba(0,0,0,0.16)",
      }}
    >
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const t = statusTone(status);
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.10em] px-1.5 py-[2px] rounded-full"
      style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color }}
    >
      <span
        className="w-1 h-1 rounded-full"
        style={{ background: t.color, boxShadow: `0 0 4px ${t.color}` }}
      />
      {status || "—"}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section: Dashboard
// ────────────────────────────────────────────────────────────────────────────
function DashboardSection() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/stats");
      setStats(r.data);
    } catch (e) {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !stats) {
    return <div className="text-center py-12 text-[var(--text-3)]">Loading…</div>;
  }
  if (!stats) return null;

  return (
    <div className="space-y-4" data-testid="admin-dashboard-section">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)]">
          Overview · {fmtDate(stats.generated_at)}
        </div>
        <button
          onClick={load}
          data-testid="admin-stats-refresh"
          className="text-[11px] text-[var(--purple-300)] inline-flex items-center gap-1 hover:opacity-80"
        >
          <RefreshCcw size={11} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Total Users"
          value={stats.users.total}
          sub={`+${stats.users.today_signups} today`}
          tone="#A78BFA"
          icon={UsersIcon}
        />
        <StatCard
          label="Activated"
          value={stats.users.activated}
          sub={`of ${stats.users.total}`}
          tone="#22C55E"
          icon={Check}
        />
        <StatCard
          label="Total Deposits"
          value={fmtUSD(stats.deposits.total_amount)}
          sub={`${stats.deposits.total_count} txns`}
          tone="#FFD36A"
          icon={ArrowDownToLine}
        />
        <StatCard
          label="Today Deposits"
          value={fmtUSD(stats.deposits.today_amount)}
          tone="#3B82F6"
          icon={ArrowDownToLine}
        />
        <StatCard
          label="Pending W/D"
          value={fmtUSD(stats.withdrawals.pending_amount)}
          sub={`${stats.withdrawals.pending_count} req.`}
          tone="#F59E0B"
          icon={ArrowUpFromLine}
        />
        <StatCard
          label="Paid W/D"
          value={fmtUSD(stats.withdrawals.paid_amount)}
          sub={`${stats.withdrawals.paid_count} done`}
          tone="#22C55E"
          icon={ArrowUpFromLine}
        />
      </div>

      <RowCard testid="admin-support-summary">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{
                background: "linear-gradient(180deg, #A78BFA30 0%, #A78BFA10 100%)",
                border: "1px solid #A78BFA55",
              }}
            >
              <LifeBuoy size={16} className="text-[#A78BFA]" />
            </div>
            <div>
              <div className="text-sm font-semibold">Support</div>
              <div className="text-[11px] text-[var(--text-3)]">
                {stats.support.open_tickets} open tickets
              </div>
            </div>
          </div>
          <ChevronRight size={16} className="text-[var(--text-3)]" />
        </div>
      </RowCard>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section: Users
// ────────────────────────────────────────────────────────────────────────────
function UsersSection() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q.trim()) params.q = q.trim();
      const r = await adminApi.get("/admin/users", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-3" data-testid="admin-users-section">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]"
          />
          <Input
            data-testid="admin-users-search"
            placeholder="Search by name, email, address…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9 bg-[var(--surface-1)] border-[var(--card3d-border)] text-[var(--text-1)] h-10"
          />
        </div>
        <Button
          data-testid="admin-users-refresh"
          onClick={load}
          className="h-10 px-3"
          variant="outline"
        >
          <RefreshCcw size={14} />
        </Button>
      </div>

      <div className="text-[10px] text-[var(--text-3)] uppercase tracking-[0.14em]">
        Showing {data.items.length} of {data.total}
      </div>

      {loading && data.items.length === 0 && (
        <div className="text-center py-10 text-[var(--text-3)]">Loading…</div>
      )}

      <div className="space-y-2">
        {data.items.map((u) => (
          <RowCard key={u.address} testid={`user-row-${u.address}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[13px] font-bold text-[var(--text-1)] truncate">
                    {u.name || "Unnamed"}
                  </span>
                  {u.is_admin && (
                    <Badge
                      className="text-[7px] py-0 px-1.5 bg-gradient-to-r from-[#FFD36A] to-[#F3BA2F] text-black"
                    >
                      <Crown size={8} className="mr-0.5" /> ADMIN
                    </Badge>
                  )}
                  {u.blocked && (
                    <Badge className="text-[7px] py-0 px-1.5 bg-red-600 text-white">
                      BLOCKED
                    </Badge>
                  )}
                </div>
                {u.email && (
                  <div className="text-[10px] text-[var(--text-2)] truncate mt-0.5">
                    {u.email}
                  </div>
                )}
                <div className="mono text-[10px] text-[var(--text-3)] truncate mt-0.5">
                  {shortAddr(u.address)}
                </div>
                <div className="text-[10px] text-[var(--text-3)] mt-0.5">
                  Rank · <span className="text-[var(--text-2)]">{u.rank}</span>
                  {" · "}Pkg ·{" "}
                  <span className="text-[var(--text-2)]">{u.package_name || "—"}</span>
                </div>
                <div className="text-[10px] text-[var(--text-3)] mt-0.5">
                  Refs · {u.direct_referrals} · Team · {u.team_size}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="mono text-[13px] font-bold">
                  {fmtUSD(u.total_deposit)}
                </div>
                <div className="text-[9px] text-[var(--text-3)] mt-0.5">
                  {fmtDate(u.created_at).split(",")[0]}
                </div>
                {u.activated ? (
                  <StatusBadge status="active" />
                ) : (
                  <StatusBadge status="pending" />
                )}
                <button
                  data-testid={`block-btn-${u.address}`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    const action = u.blocked ? "unblock" : "block";
                    const confirmMsg = u.blocked ? `Unblock ${u.name || u.address}?` : `Block ${u.name || u.address}? They will not be able to login.`;
                    if (!window.confirm(confirmMsg)) return;
                    try {
                      await adminApi.post(`/admin/users/${u.id}/${action}`);
                      toast.success(u.blocked ? "User unblocked" : "User blocked");
                      load();
                    } catch (err) {
                      toast.error(`Failed to ${action} user`);
                    }
                  }}
                  className={`mt-1 text-[8px] px-1.5 py-0.5 rounded font-bold ${u.blocked ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-red-600 hover:bg-red-500 text-white'}`}
                >
                  {u.blocked ? 'UNBLOCK' : 'BLOCK'}
                </button>
              </div>
            </div>
          </RowCard>
        ))}
        {!loading && data.items.length === 0 && (
          <div className="text-center py-10 text-[var(--text-3)]">No users found</div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section: Deposits
// ────────────────────────────────────────────────────────────────────────────
function DepositsSection() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (status !== "all") params.status = status;
      if (q.trim()) params.q = q.trim();
      const r = await adminApi.get("/admin/deposits", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load deposits");
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-3" data-testid="admin-deposits-section">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]"
          />
          <Input
            data-testid="admin-deposits-search"
            placeholder="Address or tx_hash…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9 bg-[var(--surface-1)] border-[var(--card3d-border)] h-10"
          />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger
            data-testid="admin-deposits-status"
            className="w-[120px] h-10 bg-[var(--surface-1)] border-[var(--card3d-border)]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="text-[10px] text-[var(--text-3)] uppercase tracking-[0.14em]">
        {data.items.length} of {data.total} deposits
      </div>

      {loading && data.items.length === 0 && (
        <div className="text-center py-10 text-[var(--text-3)]">Loading…</div>
      )}

      <div className="space-y-2">
        {data.items.map((d, i) => (
          <RowCard key={d.id || i} testid={`deposit-row-${d.id || i}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="mono text-[12px] font-semibold">
                    {shortAddr(d.user_address)}
                  </span>
                  <StatusBadge status={d.status} />
                </div>
                <div className="text-[10px] text-[var(--text-3)] mt-0.5">
                  Pkg · {d.package_name || "—"} · {fmtDate(d.created_at)}
                </div>
                {d.tx_hash && (
                  <a
                    href={`https://bscscan.com/tx/${d.tx_hash}`}
                    target="_blank"
                    rel="noreferrer"
                    className="mono text-[9.5px] text-[var(--purple-300)] hover:underline inline-flex items-center gap-1 mt-0.5"
                  >
                    {d.tx_hash.slice(0, 12)}…{d.tx_hash.slice(-6)}
                    <ExternalLink size={9} />
                  </a>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="mono text-[14px] font-bold text-[#22C55E]">
                  {fmtUSD(d.amount)}
                </div>
              </div>
            </div>
          </RowCard>
        ))}
        {!loading && data.items.length === 0 && (
          <div className="text-center py-10 text-[var(--text-3)]">
            No deposits found
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section: Withdrawals
// ────────────────────────────────────────────────────────────────────────────
function WithdrawalsSection() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [actionBusy, setActionBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (status !== "all") params.status = status;
      const r = await adminApi.get("/admin/withdrawals", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load withdrawals");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const action = async (wid, act) => {
    setActionBusy(wid + act);
    try {
      let url = `/admin/withdrawals/${wid}/action?action=${act}`;
      if (act === "approve") {
        // Optional payout TX hash for record-keeping (admin manually sent USDT)
        const txh = prompt(
          "Mark this withdrawal as COMPLETED?\n\n" +
          "Please confirm you have already sent the USDT to the user's address.\n\n" +
          "Optionally paste the BscScan tx-hash below for record:"
        );
        if (txh === null) {
          // user pressed Cancel
          setActionBusy(null);
          return;
        }
        if (txh) url += `&tx_hash=${encodeURIComponent(txh.trim())}`;
      }
      await adminApi.post(url);
      toast.success(act === "approve" ? "Withdrawal marked as completed" : `Withdrawal ${act}ed`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setActionBusy(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-withdrawals-section">
      <div className="flex items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger
            data-testid="admin-withdrawals-status"
            className="w-full h-10 bg-[var(--surface-1)] border-[var(--card3d-border)]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button
          data-testid="admin-withdrawals-refresh"
          onClick={load}
          variant="outline"
          className="h-10 px-3"
        >
          <RefreshCcw size={14} />
        </Button>
      </div>

      <div className="text-[10px] text-[var(--text-3)] uppercase tracking-[0.14em]">
        {data.items.length} of {data.total} withdrawals
      </div>

      {loading && data.items.length === 0 && (
        <div className="text-center py-10 text-[var(--text-3)]">Loading…</div>
      )}

      <div className="space-y-2">
        {data.items.map((w, i) => {
          const canAct = (w.status || "").toLowerCase() === "pending";
          const payoutAddr = w.payout_address || w.user_address || "";
          const copyAddr = async () => {
            if (!payoutAddr) return toast.error("No payout address available");
            try {
              await navigator.clipboard.writeText(payoutAddr);
              toast.success("Address copied — paste in your wallet to send USDT");
            } catch {
              toast.error("Could not copy. Long-press to copy manually.");
            }
          };
          return (
            <RowCard key={w.id || i} testid={`withdrawal-row-${w.id || i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  {/* User name as PRIMARY identifier — admin sees who this withdrawal is for */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className="text-[14px] font-bold truncate"
                      style={{ color: "var(--text-1)" }}
                      data-testid={`wd-user-name-${w.id}`}
                    >
                      {w.user_name || w.user_username || w.user_email || shortAddr(payoutAddr) || "—"}
                    </span>
                    <StatusBadge status={w.status} />
                  </div>
                  {/* Secondary identifiers */}
                  {(w.user_name || w.user_username) && w.user_email && (
                    <div className="text-[10px] text-[var(--text-3)] mt-0.5">
                      {w.user_email}
                      {w.user_sequential_id ? (
                        <span className="mono ml-1.5 opacity-70">#{w.user_sequential_id}</span>
                      ) : null}
                    </div>
                  )}

                  {/* Full payout address + COPY — what admin uses to manually send USDT */}
                  {payoutAddr ? (
                    <div className="flex items-center gap-1.5 mt-1.5 group" data-testid={`wd-payout-addr-${w.id}`}>
                      <div
                        className="mono text-[10px] truncate flex-1 px-2 py-1 rounded-md select-all"
                        style={{
                          background: "rgba(34,197,94,0.06)",
                          border: "1px solid rgba(34,197,94,0.20)",
                          color: "#86EFAC",
                        }}
                        title={payoutAddr}
                      >
                        {payoutAddr}
                      </div>
                      <button
                        type="button"
                        onClick={copyAddr}
                        data-testid={`wd-copy-${w.id}`}
                        className="shrink-0 h-6 w-6 rounded-md flex items-center justify-center transition-colors"
                        style={{
                          background: "rgba(34,197,94,0.10)",
                          border: "1px solid rgba(34,197,94,0.30)",
                          color: "#86EFAC",
                        }}
                        aria-label="Copy payout address"
                        title="Copy BEP-20 address"
                      >
                        <Copy size={11} />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="text-[10px] mt-1.5 px-2 py-1 rounded-md"
                      style={{
                        background: "rgba(239,68,68,0.06)",
                        border: "1px solid rgba(239,68,68,0.25)",
                        color: "#FCA5A5",
                      }}
                    >
                      ⚠ No payout wallet — contact user via {w.user_email || "support"}
                    </div>
                  )}

                  {w.user_email && !(w.user_name || w.user_username) && (
                    <div className="text-[10px] text-[var(--text-3)] mt-1">
                      Email: <span className="text-[var(--text-2)]">{w.user_email}</span>
                    </div>
                  )}

                  <div className="text-[10px] text-[var(--text-3)] mt-1">
                    Fee · <span className="text-[var(--text-2)]">{fmtUSD(w.fee_amount ?? w.fee)}</span>
                    {" · Net · "}
                    <span className="text-[var(--text-2)] font-semibold">{fmtUSD(w.net_amount)}</span>
                  </div>
                  <div className="text-[10px] text-[var(--text-3)] mt-0.5">
                    {fmtDate(w.created_at)}
                  </div>
                  {w.payout_tx_hash && (
                    <a
                      href={`https://bscscan.com/tx/${w.payout_tx_hash}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mono text-[9.5px] text-[#22C55E] hover:underline inline-flex items-center gap-1 mt-0.5"
                    >
                      Paid · {w.payout_tx_hash.slice(0, 10)}…{w.payout_tx_hash.slice(-6)}
                      <ExternalLink size={9} />
                    </a>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="mono text-[14px] font-bold text-[#F59E0B]">
                    {fmtUSD(w.amount)}
                  </div>
                </div>
              </div>

              {canAct && (
                <div className="flex gap-2 mt-2.5 pt-2.5 border-t border-[var(--card3d-border)]">
                  <button
                    data-testid={`wd-approve-${w.id}`}
                    disabled={actionBusy === w.id + "approve"}
                    onClick={() => action(w.id, "approve")}
                    className="flex-1 h-8 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0.06) 100%)",
                      border: "1px solid rgba(34,197,94,0.45)",
                      color: "#22C55E",
                    }}
                    title="Confirm USDT payout was sent and mark withdrawal as completed"
                  >
                    <Check size={12} /> Approve &amp; Complete
                  </button>
                  <button
                    data-testid={`wd-reject-${w.id}`}
                    disabled={actionBusy === w.id + "reject"}
                    onClick={() => action(w.id, "reject")}
                    className="flex-1 h-8 rounded-lg text-[11px] font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(248,113,113,0.18) 0%, rgba(248,113,113,0.06) 100%)",
                      border: "1px solid rgba(248,113,113,0.45)",
                      color: "#F87171",
                    }}
                    title="Refund the amount back to user's wallet"
                  >
                    <X size={12} /> Reject &amp; Refund
                  </button>
                </div>
              )}
            </RowCard>
          );
        })}
        {!loading && data.items.length === 0 && (
          <div className="text-center py-10 text-[var(--text-3)]">
            No withdrawals found
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section: Rank Members
// ────────────────────────────────────────────────────────────────────────────
function RanksSection() {
  const [data, setData] = useState({ rank_counts: [], items: [] });
  const [loading, setLoading] = useState(true);
  const [selectedRank, setSelectedRank] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (selectedRank) params.rank = selectedRank;
      const r = await adminApi.get("/admin/rank-members", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load ranks");
    } finally {
      setLoading(false);
    }
  }, [selectedRank]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-3" data-testid="admin-ranks-section">
      <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">
        Rank Distribution
      </div>
      <div className="grid grid-cols-3 gap-2">
        {data.rank_counts.map((r) => (
          <button
            key={r.rank}
            data-testid={`rank-pill-${r.rank}`}
            onClick={() =>
              setSelectedRank(selectedRank === r.rank ? null : r.rank)
            }
            className="rounded-xl p-2.5 text-left transition-all"
            style={{
              background:
                selectedRank === r.rank
                  ? "linear-gradient(160deg, #A78BFA28 0%, #A78BFA08 100%)"
                  : "linear-gradient(160deg, var(--surface-1) 0%, var(--surface-2) 100%)",
              border:
                selectedRank === r.rank
                  ? "1.5px solid #A78BFA"
                  : "1px solid var(--card3d-border)",
              boxShadow:
                selectedRank === r.rank
                  ? "0 0 0 1px #A78BFA33, 0 6px 16px -4px #A78BFA55"
                  : "inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
          >
            <div className="text-[9px] uppercase tracking-[0.14em] text-[var(--text-3)]">
              {r.rank}
            </div>
            <div className="mono text-lg font-black mt-0.5">{r.count}</div>
          </button>
        ))}
        {data.rank_counts.length === 0 && !loading && (
          <div className="col-span-3 text-center text-[var(--text-3)] text-xs py-4">
            No ranks yet
          </div>
        )}
      </div>

      {selectedRank && (
        <>
          <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mt-4">
            {selectedRank} Members ({data.items.length})
          </div>
          <div className="space-y-2">
            {data.items.map((u) => (
              <RowCard key={u.address} testid={`rank-member-${u.address}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-bold text-[var(--text-1)] truncate">
                      {u.name || "Unnamed"}
                    </div>
                    {u.email && (
                      <div className="text-[10px] text-[var(--text-2)] truncate mt-0.5">
                        {u.email}
                      </div>
                    )}
                    <div className="mono text-[10px] text-[var(--text-3)] truncate mt-0.5">
                      {shortAddr(u.address)}
                    </div>
                    <div className="text-[10px] text-[var(--text-3)] mt-0.5">
                      Refs · {u.direct_referrals} · Team · {u.team_size}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="mono text-[13px] font-bold">
                      {fmtUSD(u.total_deposit)}
                    </div>
                  </div>
                </div>
              </RowCard>
            ))}
            {data.items.length === 0 && (
              <div className="text-center py-6 text-[var(--text-3)] text-xs">
                No members in this rank
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Section: Support
// ────────────────────────────────────────────────────────────────────────────
function SupportSection() {
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (status !== "all") params.status = status;
      const r = await adminApi.get("/admin/support", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-3" data-testid="admin-support-section">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger
          data-testid="admin-support-status"
          className="h-10 bg-[var(--surface-1)] border-[var(--card3d-border)]"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Tickets</SelectItem>
          <SelectItem value="open">Open</SelectItem>
          <SelectItem value="resolved">Resolved</SelectItem>
        </SelectContent>
      </Select>

      <div className="text-[10px] text-[var(--text-3)] uppercase tracking-[0.14em]">
        {data.items.length} of {data.total} tickets
      </div>

      {loading && data.items.length === 0 && (
        <div className="text-center py-10 text-[var(--text-3)]">Loading…</div>
      )}

      <div className="space-y-2">
        {data.items.map((t) => (
          <RowCard key={t.id} testid={`ticket-row-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] font-semibold truncate">
                    {t.subject}
                  </span>
                  <StatusBadge status={t.status} />
                </div>
                <div className="text-[10px] text-[var(--text-3)] mt-0.5 mono">
                  {shortAddr(t.user_address)} · {t.category || "general"}
                </div>
                <div className="text-[10px] text-[var(--text-3)] mt-0.5">
                  {fmtDate(t.created_at)} · {t.messages?.length || 0} msgs
                </div>
              </div>
            </div>
          </RowCard>
        ))}
        {!loading && data.items.length === 0 && (
          <div className="text-center py-10 text-[var(--text-3)]">
            No tickets found
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main Admin Page
// ────────────────────────────────────────────────────────────────────────────
export default function Admin() {
  const navigate = useNavigate();
  const [active, setActive] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUsername, setAdminUsername] = useState("");

  // Verify admin access via /admin-auth/me (NEW: username/password based)
  useEffect(() => {
    let cancelled = false;
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!adminToken) {
      navigate("/control", { replace: true });
      return;
    }
    (async () => {
      try {
        const r = await adminApi.get("/admin-auth/me");
        if (!cancelled) {
          setIsAdmin(r.data?.is_admin === true);
          setAdminUsername(r.data?.username || "");
        }
      } catch (e) {
        if (!cancelled) {
          setIsAdmin(false);
          localStorage.removeItem(ADMIN_TOKEN_KEY);
          navigate("/control", { replace: true });
        }
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    navigate("/control", { replace: true });
  };

  const activeItem = useMemo(
    () => NAV_ITEMS.find((x) => x.id === active) || NAV_ITEMS[0],
    [active]
  );

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--surface-0)]">
        <div className="text-[var(--text-3)] text-sm">Verifying admin access…</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--surface-0)] px-6 text-center gap-4">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{
            background: "linear-gradient(180deg, #F8717130 0%, #F8717110 100%)",
            border: "1px solid #F8717155",
          }}
        >
          <ShieldAlert size={28} className="text-[#F87171]" />
        </div>
        <div className="text-lg font-bold">Admin Access Required</div>
        <div className="text-sm text-[var(--text-3)] max-w-sm">
          Please sign in with your administrator credentials to access this panel.
        </div>
        <Button onClick={() => navigate("/control", { replace: true })} className="mt-2">
          Sign In
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-0)] text-[var(--text-1)] pb-16">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{
          background: "color-mix(in srgb, var(--surface-1) 80%, transparent)",
          borderBottom: "1px solid var(--card3d-border)",
        }}
      >
        <div className="max-w-[760px] mx-auto px-3 py-2.5 flex items-center justify-between gap-2">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="admin-hamburger-button"
                className="w-10 h-10 rounded-xl bg-[var(--surface-2)] border border-[var(--card3d-border)] hover:border-white/20 flex items-center justify-center transition-colors"
                aria-label="Open menu"
              >
                <Menu size={18} className="text-[var(--text-2)]" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="bg-[var(--surface-1)] border-r border-[var(--card3d-border)] text-[var(--text-1)] w-[300px] p-0"
            >
              <div className="px-4 py-4 border-b border-[var(--card3d-border)]">
                <div className="flex items-center gap-2.5">
                  <Logo size={36} glow />
                  <div className="min-w-0">
                    <div className="font-bold text-sm">SMART HUB</div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#FFD36A] flex items-center gap-1">
                      <Crown size={9} /> Admin Panel
                    </div>
                  </div>
                </div>
                {adminUsername && (
                  <div className="mt-2.5 pt-2.5 border-t border-[var(--card3d-border)]">
                    <div className="text-[9px] uppercase tracking-[0.14em] text-[var(--text-3)]">
                      Signed in as
                    </div>
                    <div className="text-[11px] font-medium text-[var(--text-2)] truncate mt-0.5">
                      {adminUsername}
                    </div>
                  </div>
                )}
              </div>

              <nav className="p-2 space-y-1">
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isActive = active === item.id;
                  return (
                    <button
                      key={item.id}
                      data-testid={`admin-nav-${item.id}`}
                      onClick={() => {
                        setActive(item.id);
                        setMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors"
                      style={{
                        background: isActive
                          ? "linear-gradient(180deg, rgba(124,58,237,0.18) 0%, rgba(124,58,237,0.04) 100%)"
                          : "transparent",
                        border: isActive
                          ? "1px solid rgba(124,58,237,0.45)"
                          : "1px solid transparent",
                      }}
                    >
                      <span
                        className="mono text-[10px] font-bold w-7 shrink-0"
                        style={{
                          color: isActive ? "#A78BFA" : "var(--text-3)",
                        }}
                      >
                        {item.idx}
                      </span>
                      <Icon
                        size={16}
                        style={{
                          color: isActive ? "#A78BFA" : "var(--text-2)",
                        }}
                      />
                      <span
                        className="text-sm font-medium"
                        style={{
                          color: isActive ? "var(--text-1)" : "var(--text-2)",
                        }}
                      >
                        {item.label}
                      </span>
                      {isActive && (
                        <ChevronRight
                          size={14}
                          className="ml-auto text-[#A78BFA]"
                        />
                      )}
                    </button>
                  );
                })}
              </nav>

              <div className="p-2 mt-4 border-t border-[var(--card3d-border)]">
                <button
                  data-testid="admin-logout"
                  onClick={() => {
                    setMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm text-[#F87171] hover:bg-[rgba(248,113,113,0.08)]"
                >
                  <LogOut size={16} /> Logout
                </button>
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex-1 text-center min-w-0">
            <div className="text-[9px] uppercase tracking-[0.18em] text-[#FFD36A] flex items-center justify-center gap-1">
              <Crown size={9} /> Admin Panel
            </div>
            <div className="text-sm font-bold truncate">
              {activeItem.idx} · {activeItem.label}
            </div>
          </div>

          <ThemeToggle />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[760px] mx-auto px-3 py-4">
        {active === "dashboard" && <DashboardSection />}
        {active === "users" && <UsersSection />}
        {active === "deposits" && <DepositsSection />}
        {active === "withdrawals" && <WithdrawalsSection />}
        {active === "wallet-adjust" && <WalletAdjustSection />}
        {active === "ranks" && <RanksSection />}
        {active === "support" && <SupportSection />}
      </main>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Wallet Adjustment Section — Admin can credit/debit any user's wallet
// Search by Gmail/Email (primary) or Name. Full audit log below.
// ────────────────────────────────────────────────────────────────────────────
function WalletAdjustSection() {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [adjustType, setAdjustType] = useState("credit"); // credit | debit
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Debounced search by email/name
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setUsers([]);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await adminApi.get(`/admin/users?q=${encodeURIComponent(q)}&limit=10`);
        setUsers(r.data?.items || []);
      } catch (e) {
        toast.error("Search failed");
        setUsers([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await adminApi.get("/admin/wallet-adjustments?limit=20");
      setHistory(r.data?.items || []);
    } catch (e) {
      // silent
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const refreshSelectedUser = async (uid) => {
    try {
      const r = await adminApi.get(`/admin/users?q=${encodeURIComponent(uid)}&limit=1`);
      // Server doesn't search by id, so fall back to email-based refresh
      if (selectedUser?.email) {
        const r2 = await adminApi.get(`/admin/users?q=${encodeURIComponent(selectedUser.email)}&limit=5`);
        const fresh = (r2.data?.items || []).find((u) => u.id === uid);
        if (fresh) setSelectedUser(fresh);
      }
    } catch { /* noop */ }
  };

  const handleSubmit = async () => {
    if (!selectedUser) {
      toast.error("Select a user first");
      return;
    }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (adjustType === "debit" && amt > (selectedUser.wallet_balance || 0)) {
      toast.error(`User has only $${(selectedUser.wallet_balance || 0).toFixed(2)} USDT`);
      return;
    }
    setSubmitting(true);
    try {
      const r = await adminApi.post(`/admin/users/${selectedUser.id}/wallet-adjust`, {
        amount: amt,
        type: adjustType,
        reason: reason.trim() || `Manual ${adjustType} by admin`,
      });
      const action = adjustType === "credit" ? "credited to" : "debited from";
      toast.success(`$${amt.toFixed(2)} ${action} ${selectedUser.email || selectedUser.name}`, {
        description: `New balance: $${(r.data?.user?.wallet_balance || 0).toFixed(2)}`,
      });
      setAmount("");
      setReason("");
      // Refresh selected user's balance
      if (r.data?.user) {
        setSelectedUser((prev) => ({ ...prev, wallet_balance: r.data.user.wallet_balance }));
      }
      // Refresh history
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Adjustment failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-wallet-adjust-section">
      {/* Search Card */}
      <div className="rounded-2xl p-4 border border-[var(--card3d-border)] bg-[var(--surface-1)]">
        <div className="flex items-center gap-2 mb-3">
          <WalletIcon size={16} className="text-[#FFD36A]" />
          <div className="text-sm font-bold text-[var(--text-1)]">Search User (Gmail or Name)</div>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-3)]" />
          <Input
            data-testid="wallet-adjust-search-input"
            placeholder="e.g. user@gmail.com or John"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 bg-[var(--surface-0)] border-[var(--card3d-border)] text-[var(--text-1)]"
          />
          {searching && (
            <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-3)] animate-spin" />
          )}
        </div>
        {users.length > 0 && (
          <div className="mt-3 space-y-1.5 max-h-[280px] overflow-y-auto">
            {users.map((u) => {
              const isSelected = selectedUser?.id === u.id;
              return (
                <button
                  key={u.id}
                  data-testid={`wallet-adjust-user-${u.id}`}
                  onClick={() => setSelectedUser(u)}
                  className="w-full text-left p-3 rounded-xl border transition-colors"
                  style={{
                    background: isSelected ? "rgba(255,211,106,0.10)" : "var(--surface-0)",
                    borderColor: isSelected ? "rgba(255,211,106,0.45)" : "var(--card3d-border)",
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-[var(--text-1)] truncate">
                        {u.name || "—"}
                      </div>
                      <div className="text-[11px] text-[var(--text-3)] truncate mono">
                        {u.email || shortAddr(u.address) || "—"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[9px] uppercase tracking-[0.14em] text-[var(--text-3)]">Balance</div>
                      <div className="mono text-sm font-bold" style={{ color: "#86EFAC" }}>
                        {fmtUSD(u.wallet_balance)}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {query && !searching && users.length === 0 && (
          <div className="mt-3 text-xs text-[var(--text-3)] text-center py-4">No users found</div>
        )}
      </div>

      {/* Selected User + Adjust Form */}
      {selectedUser && (
        <div className="rounded-2xl p-4 border border-[var(--card3d-border)] bg-[var(--surface-1)] space-y-4" data-testid="wallet-adjust-form">
          {/* Selected user header */}
          <div className="flex items-start justify-between gap-3 pb-3 border-b border-[var(--card3d-border)]">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-0.5">Selected User</div>
              <div className="text-sm font-bold text-[var(--text-1)] truncate">{selectedUser.name || "—"}</div>
              <div className="text-[11px] text-[var(--text-2)] truncate mono">{selectedUser.email || shortAddr(selectedUser.address)}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-0.5">Wallet Balance</div>
              <div className="mono text-lg font-bold" style={{ color: "#86EFAC" }} data-testid="wallet-adjust-selected-balance">
                {fmtUSD(selectedUser.wallet_balance)}
              </div>
            </div>
          </div>

          {/* Credit/Debit toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="wallet-adjust-type-credit"
              onClick={() => setAdjustType("credit")}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-semibold transition-colors"
              style={{
                background: adjustType === "credit" ? "rgba(34,197,94,0.14)" : "var(--surface-0)",
                borderColor: adjustType === "credit" ? "rgba(34,197,94,0.50)" : "var(--card3d-border)",
                color: adjustType === "credit" ? "#86EFAC" : "var(--text-2)",
              }}
            >
              <Plus size={14} /> Add Fund
            </button>
            <button
              data-testid="wallet-adjust-type-debit"
              onClick={() => setAdjustType("debit")}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-semibold transition-colors"
              style={{
                background: adjustType === "debit" ? "rgba(248,113,113,0.14)" : "var(--surface-0)",
                borderColor: adjustType === "debit" ? "rgba(248,113,113,0.50)" : "var(--card3d-border)",
                color: adjustType === "debit" ? "#F87171" : "var(--text-2)",
              }}
            >
              <Minus size={14} /> Minus Fund
            </button>
          </div>

          {/* Amount */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-1.5">Amount (USDT)</div>
            <Input
              data-testid="wallet-adjust-amount-input"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-[var(--surface-0)] border-[var(--card3d-border)] text-[var(--text-1)] mono text-lg"
            />
          </div>

          {/* Reason */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-1.5">Reason (Optional)</div>
            <Input
              data-testid="wallet-adjust-reason-input"
              placeholder="e.g. Manual correction, refund, bonus"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="bg-[var(--surface-0)] border-[var(--card3d-border)] text-[var(--text-1)]"
              maxLength={300}
            />
          </div>

          {/* Submit */}
          <Button
            data-testid="wallet-adjust-submit-button"
            onClick={handleSubmit}
            disabled={submitting || !amount}
            className="w-full font-semibold"
            style={{
              background: adjustType === "credit"
                ? "linear-gradient(135deg, #86EFAC 0%, #22C55E 100%)"
                : "linear-gradient(135deg, #FCA5A5 0%, #EF4444 100%)",
              color: adjustType === "credit" ? "#052E16" : "#450A0A",
            }}
          >
            {submitting ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Processing…</>
            ) : (
              <>{adjustType === "credit" ? <Plus size={14} className="mr-1.5" /> : <Minus size={14} className="mr-1.5" />}
              Confirm {adjustType === "credit" ? "Add" : "Minus"} {amount ? `$${parseFloat(amount).toFixed(2)}` : ""}</>
            )}
          </Button>
        </div>
      )}

      {/* Audit Log */}
      <div className="rounded-2xl p-4 border border-[var(--card3d-border)] bg-[var(--surface-1)]" data-testid="wallet-adjust-history">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <History size={14} className="text-[#A78BFA]" />
            <div className="text-sm font-bold text-[var(--text-1)]">Adjustment History</div>
          </div>
          <button onClick={loadHistory} className="text-[var(--text-3)] hover:text-[var(--text-1)]" data-testid="wallet-adjust-refresh-history">
            <RefreshCcw size={13} className={historyLoading ? "animate-spin" : ""} />
          </button>
        </div>
        {historyLoading && history.length === 0 ? (
          <div className="text-center py-6 text-xs text-[var(--text-3)]">Loading…</div>
        ) : history.length === 0 ? (
          <div className="text-center py-6 text-xs text-[var(--text-3)]">No adjustments yet</div>
        ) : (
          <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
            {history.map((a) => {
              const isCredit = a.type === "credit";
              const accent = isCredit ? "#86EFAC" : "#F87171";
              const sign = isCredit ? "+" : "−";
              return (
                <div
                  key={a.id}
                  className="flex items-center gap-3 p-2.5 rounded-lg border border-[var(--card3d-border)] bg-[var(--surface-0)]"
                  data-testid={`wallet-adjust-history-row-${a.id}`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{
                      background: isCredit ? "rgba(34,197,94,0.14)" : "rgba(248,113,113,0.14)",
                      color: accent,
                    }}
                  >
                    {isCredit ? <Plus size={13} /> : <Minus size={13} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-[var(--text-1)] truncate">{a.user_name || a.user_email || "—"}</div>
                    <div className="text-[10px] text-[var(--text-3)] truncate mono">{a.user_email}</div>
                    {a.reason ? (
                      <div className="text-[10px] text-[var(--text-3)] truncate italic mt-0.5">"{a.reason}"</div>
                    ) : null}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="mono text-sm font-bold" style={{ color: accent }}>
                      {sign}${(a.amount || 0).toFixed(2)}
                    </div>
                    <div className="text-[9px] text-[var(--text-3)] mono">{fmtDate(a.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
